/**
 * MessageItem - Single message: avatar, markdown, reactions, reply count, actions.
 * Supports grouped display (isGroupStart=false) for consecutive messages from same user.
 */

import React, { useCallback, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { chatReactionsApi, chatMessagesApi, chatPinsApi, chatBookmarksApi } from '../../services/chatApi';
import MarkdownRenderer from './MarkdownRenderer';
import AttachmentPreview from './AttachmentPreview';
import UserAvatar from '../UserAvatar';
import type { ChatMessage } from '../../types/chat';

interface MessageItemProps {
  message: ChatMessage;
  isGroupStart?: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({ message, isGroupStart = true }) => {
  const { user } = useAuth();
  const { setThreadParentMessage } = useChat();
  const [showActions, setShowActions] = useState(false);

  const isOwn = user?.id === message.user_id;
  const time = new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const handleReaction = useCallback(async (emoji: string) => {
    await chatReactionsApi.toggle(message.id, emoji);
  }, [message.id]);

  const handleThreadClick = useCallback(() => {
    setThreadParentMessage(message);
  }, [message, setThreadParentMessage]);

  const handlePin = useCallback(async () => {
    await chatPinsApi.toggle(message.id);
  }, [message.id]);

  const handleBookmark = useCallback(async () => {
    await chatBookmarksApi.add(message.id);
  }, [message.id]);

  const handleDelete = useCallback(async () => {
    await chatMessagesApi.delete(message.id);
  }, [message.id]);

  if (message.is_deleted) {
    return (
      <div className={`px-4 ${isGroupStart ? 'py-1' : 'py-0.5'}`}>
        <span className="text-sm text-gray-400 italic">[deleted]</span>
      </div>
    );
  }

  // Continuation message (grouped) - compact layout without avatar/name
  if (!isGroupStart) {
    return (
      <div
        className={`group px-4 py-0.5 hover:bg-gray-50 relative flex ${isOwn ? 'justify-end' : 'justify-start'}`}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        <div className={`max-w-[75%] ${isOwn ? 'flex flex-col items-end' : ''}`}>
          <div className={`flex items-start ${isOwn ? 'flex-row-reverse' : ''}`}>
            {/* Spacer to align with avatar width + gap */}
            <div className="w-8 flex-shrink-0 flex items-center justify-center">
              <span className="text-[10px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                {time}
              </span>
            </div>
            <div className={`min-w-0 ${isOwn ? 'text-right mr-3' : 'ml-3'}`}>
              {/* Content */}
              {message.content.trim() && (
                <div className={`text-sm rounded-lg px-3 py-1.5 inline-block ${
                  isOwn
                    ? 'bg-blue-500 text-white text-left'
                    : 'bg-gray-100 text-gray-800 text-left'
                }`}>
                  <MarkdownRenderer content={message.content} />
                </div>
              )}

              {/* Attachments */}
              {message.attachments && message.attachments.length > 0 && (
                <div className={`flex flex-wrap gap-2 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                  {message.attachments.map(att => (
                    <AttachmentPreview key={att.id} attachment={att} />
                  ))}
                </div>
              )}

              {/* Reactions */}
              {message.reactions && message.reactions.length > 0 && (
                <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                  {message.reactions.map(r => (
                    <button
                      key={r.emoji}
                      onClick={() => handleReaction(r.emoji)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                        r.user_ids.includes(user?.id || 0)
                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span>{r.emoji}</span>
                      <span>{r.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Thread indicator */}
              {message.reply_count > 0 && (
                <button
                  onClick={handleThreadClick}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Hover actions */}
        {showActions && (
          <div className={`absolute top-0 flex items-center bg-white border rounded shadow-sm ${isOwn ? 'left-4' : 'right-4'}`}>
            <button onClick={() => handleReaction('\u{1F44D}')} className="p-1.5 hover:bg-gray-100 text-sm" title="React">+</button>
            <button onClick={handleThreadClick} className="p-1.5 hover:bg-gray-100 text-xs text-gray-500" title="Reply in thread">T</button>
            <button onClick={handlePin} className="p-1.5 hover:bg-gray-100 text-xs text-gray-500" title="Pin">P</button>
            <button onClick={handleBookmark} className="p-1.5 hover:bg-gray-100 text-xs text-gray-500" title="Bookmark">B</button>
            {isOwn && (
              <button onClick={handleDelete} className="p-1.5 hover:bg-red-50 text-xs text-red-400" title="Delete">X</button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full message (group start) - avatar, name, timestamp
  return (
    <div
      className={`group px-4 py-1.5 hover:bg-gray-50 relative flex ${isOwn ? 'justify-end' : 'justify-start'}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={`max-w-[75%] ${isOwn ? 'flex flex-col items-end' : ''}`}>
        <div className={`flex items-start gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}>
          {/* Avatar */}
          <UserAvatar
            user={{
              id: message.user_id,
              full_name: message.full_name,
              username: message.username,
              avatar_url: message.avatar_url,
            }}
            size="md"
            className="mt-0.5"
          />

          <div className={`min-w-0 ${isOwn ? 'text-right' : ''}`}>
            {/* Header */}
            <div className={`flex items-baseline gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
              <span className="font-semibold text-sm text-gray-900">
                {message.full_name || message.username || `User ${message.user_id}`}
              </span>
              <span className="text-xs text-gray-400">{time}</span>
              {message.is_edited && (
                <span className="text-xs text-gray-400">(edited)</span>
              )}
            </div>

            {/* Content */}
            {message.content.trim() && (
              <div className={`text-sm mt-0.5 rounded-lg px-3 py-1.5 inline-block ${
                isOwn
                  ? 'bg-blue-500 text-white text-left'
                  : 'bg-gray-100 text-gray-800 text-left'
              }`}>
                <MarkdownRenderer content={message.content} />
              </div>
            )}

            {/* Attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div className={`flex flex-wrap gap-2 mt-2 ${isOwn ? 'justify-end' : ''}`}>
                {message.attachments.map(att => (
                  <AttachmentPreview key={att.id} attachment={att} />
                ))}
              </div>
            )}

            {/* Reactions */}
            {message.reactions && message.reactions.length > 0 && (
              <div className={`flex flex-wrap gap-1 mt-1.5 ${isOwn ? 'justify-end' : ''}`}>
                {message.reactions.map(r => (
                  <button
                    key={r.emoji}
                    onClick={() => handleReaction(r.emoji)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${
                      r.user_ids.includes(user?.id || 0)
                        ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>{r.emoji}</span>
                    <span>{r.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Thread indicator */}
            {message.reply_count > 0 && (
              <button
                onClick={handleThreadClick}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hover actions */}
      {showActions && (
        <div className={`absolute top-0 flex items-center bg-white border rounded shadow-sm ${isOwn ? 'left-4' : 'right-4'}`}>
          <button
            onClick={() => handleReaction('\u{1F44D}')}
            className="p-1.5 hover:bg-gray-100 text-sm"
            title="React"
          >
            +
          </button>
          <button
            onClick={handleThreadClick}
            className="p-1.5 hover:bg-gray-100 text-xs text-gray-500"
            title="Reply in thread"
          >
            T
          </button>
          <button
            onClick={handlePin}
            className="p-1.5 hover:bg-gray-100 text-xs text-gray-500"
            title="Pin"
          >
            P
          </button>
          <button
            onClick={handleBookmark}
            className="p-1.5 hover:bg-gray-100 text-xs text-gray-500"
            title="Bookmark"
          >
            B
          </button>
          {isOwn && (
            <button
              onClick={handleDelete}
              className="p-1.5 hover:bg-red-50 text-xs text-red-400"
              title="Delete"
            >
              X
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageItem;
